import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * A custom hook that syncs state with a URL search parameter.
 * Supports multi-value parameters as arrays.
 * @param key The search parameter key to sync with.
 * @param defaultValue The default value for the state.
 * @returns A stateful value, and a function to update it.
 */
function useMultiValueParam(
    key: string,
    defaultValue: number[] = []
): [number[], (newValues: number[]) => void] {
    const [searchParams, setSearchParams] = useSearchParams();
    const paramValues = searchParams.getAll(key);

    // Initialize state with values from the URL or the default value
    const [state, setState] = useState<number[]>(
        paramValues.length > 0 ? paramValues.map(Number) : defaultValue
    );

    const setMultiValueParam = useCallback(
        (newValues: number[]) => {
            // Update state
            setState(newValues);

            // Update search parameters
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete(key); // Remove all existing values for the key
            newValues.forEach((value) => {
                newSearchParams.append(key, value.toString()); // Append each new value
            });
            setSearchParams(newSearchParams);
        },
        [key, searchParams, setSearchParams]
    );

    return [state, setMultiValueParam];
}

export default useMultiValueParam;
